import type {
  BeforePlugin,
  ElementInteractionsOptions,
  EnrichmentPlugin,
  Event as AmplitudeEvent,
} from '@amplitude/analytics-browser/lib/esm/types';
import {DEFAULT_CSS_SELECTOR_ALLOWLIST} from '@amplitude/analytics-browser/lib/esm/types';

import {
  AMPLITUDE_PAGE_URL_ALLOWLIST,
  AMPLITUDE_PRODUCT_AREA_ALLOWLISTS,
  toAmplitudePageUrlAllowlistRegexes,
} from 'analytics/amplitudePageUrlAllowlist';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getPrefixToTypename} from 'graphql-client/APIIDPrefixToTypename';
import APIUtil from 'util/APIUtil';
import pick from 'util/pick';

/**
 * How aggressively event properties are redacted before leaving the browser.
 * Allowlisted rollout routes default to MASK_READABLE_TEXT unless
 * ANALYTICS_GLOBAL_MASK_AUTOCAPTURE_TEXT is false; other routes keep
 * readable UI text. Opaque Benchling API IDs are kept; vanity name suffixes are
 * replaced with a fixed mask and URL query/hash are always sanitized.
 */
export enum AmplitudeRedactionLevel {
  SANITIZE_URLS_ONLY = 'SANITIZE_URLS_ONLY',
  MASK_READABLE_TEXT = 'MASK_READABLE_TEXT',
}

function getAllApiIdPrefixes(): ReadonlyArray<string> {
  const prefixes = new Set(Object.keys(getPrefixToTypename()));
  for (const type of Object.values(ModelTypeConstants)) {
    const prefix = APIUtil.typeToApiPrefix(type);
    if (prefix) {
      prefixes.add(prefix);
    }
  }
  // Longest first so shorter prefixes cannot partially match longer ones.
  return [...prefixes].sort((a, b) => b.length - a.length);
}

/**
 * Opaque API ID is prefix_ + alphanumeric token. Optional `-vanity` name slug (from object name)
 * is customer data and must be replaced with a fixed mask while keeping the opaque ID.
 */
function buildBenchlingIdWithOptionalVanityPatternString(prefixes: ReadonlyArray<string>): string {
  return `\\b(${prefixes.join('|')})_([A-Za-z0-9]+)(-[A-Za-z0-9_-]+)?\\b`;
}

const BENCHLING_ID_WITH_OPTIONAL_VANITY_REGEX = new RegExp(
  buildBenchlingIdWithOptionalVanityPatternString(getAllApiIdPrefixes()),
  'g'
);
// Routes that match get MASK_READABLE_TEXT; derived from registry (maskReadableText defaults true).
const MASK_READABLE_TEXT_PATH_PATTERNS = AMPLITUDE_PRODUCT_AREA_ALLOWLISTS.filter(
  (area) => area.maskReadableText !== false
).flatMap((area) => toAmplitudePageUrlAllowlistRegexes(area.urlPatterns));

const SANITIZED_URL_PROPERTY_KEYS = [
  '[Amplitude] Page URL',
  '[Amplitude] Page Path',
  '[Amplitude] Page Location',
  '[Amplitude] Previous Page Location',
  'Page Path',
  'Page Location',
  '[Amplitude] Element Href',
  'page_path',
  'page_url',
  'page_location',
];

/** Autocapture properties dropped before events leave the browser. */
const DROPPED_EVENT_PROPERTY_KEYS = ['Page Title', '[Amplitude] Page Title', 'page_title'];

/**
 * Amplitude stores matching data-test-* attrs under this property, with the
 * `data-test-` prefix stripped from each key (see DataTestAttrs field names).
 */
const ELEMENT_ATTRIBUTES_PROPERTY_KEY = '[Amplitude] Element Attributes';

/** Keep stable UI selectors; drop `index`, `key`, and ad-hoc attrs (often customer text/IDs/timestamps). */
const ALLOWED_ELEMENT_ATTRIBUTE_KEYS = ['component', 'element'];

/** DOM ancestry of the interacted element, from the element itself up to (excluding) `<html>`. */
const ELEMENT_HIERARCHY_PROPERTY_KEY = '[Amplitude] Element Hierarchy';

/**
 * Structural keys kept on each hierarchy node. `id` and `classes` are dropped because both can
 * embed customer-derived values (object names, API IDs).
 */
const ALLOWED_ELEMENT_HIERARCHY_NODE_KEYS = ['tag', 'index', 'indexOfType', 'prevSib'];

/**
 * Hierarchy `attrs` keys are full attribute names, unlike the prefix-stripped
 * ELEMENT_ATTRIBUTES_PROPERTY_KEY map. Reuse the same data-test-* allowlist, plus `type`.
 * Every other attr (title, placeholder, aria-*, ad-hoc data-*) can carry customer text.
 */
const ALLOWED_ELEMENT_HIERARCHY_ATTRIBUTE_KEYS = [
  'type',
  ...ALLOWED_ELEMENT_ATTRIBUTE_KEYS.map((key) => `data-test-${key}`),
];

/** Non-URL string properties: mask vanity Benchling IDs only (no URL parsing). */
const SANITIZED_TEXT_PROPERTY_KEYS = [
  '[Amplitude] Element Text',
  '[Amplitude] Element Parent Label',
  '[Amplitude] Element Aria Label',
];

/** UI text properties replaced with a fixed mask when redaction level is MASK_READABLE_TEXT. */
const READABLE_TEXT_PROPERTY_KEYS = [
  '[Amplitude] Element Text',
  '[Amplitude] Element Parent Label',
  '[Amplitude] Element Aria Label',
];

const MASKED_TEXT = '*****';

export function getAmplitudeElementInteractionsOptions(): ElementInteractionsOptions {
  return {
    pageUrlAllowlist: AMPLITUDE_PAGE_URL_ALLOWLIST,
    // Capture data-test-* attrs; privacy plugin keeps only component/element values.
    dataAttributePrefix: 'data-test-',
    visualTaggingOptions: {
      enabled: true,
    },
    viewportContentUpdated: {
      enabled: false,
    },
    cssSelectorAllowlist: ['[tabindex="0"]', ...DEFAULT_CSS_SELECTOR_ALLOWLIST],
  };
}

function getRouteRedactionLevel(path?: string): AmplitudeRedactionLevel {
  if (!BenchlingConfig.ANALYTICS_GLOBAL_MASK_AUTOCAPTURE_TEXT) {
    return AmplitudeRedactionLevel.SANITIZE_URLS_ONLY;
  }
  const pathname = path ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  if (MASK_READABLE_TEXT_PATH_PATTERNS.some((pattern) => pattern.test(pathname))) {
    return AmplitudeRedactionLevel.MASK_READABLE_TEXT;
  }
  return AmplitudeRedactionLevel.SANITIZE_URLS_ONLY;
}

function sanitizeBenchlingIds(value: string): string {
  return value.replace(
    BENCHLING_ID_WITH_OPTIONAL_VANITY_REGEX,
    (_match, prefix: string, opaqueId: string, vanitySuffix: string | undefined) =>
      vanitySuffix ? `${prefix}_${opaqueId}-${MASKED_TEXT}` : `${prefix}_${opaqueId}`
  );
}

function sanitizePathSegments(pathname: string): string {
  return pathname
    .split('/')
    .map((segment) => sanitizeBenchlingIds(segment))
    .join('/');
}

/**
 * Sanitize URLs before events leave the browser: strip query/hash, keep opaque Benchling API IDs,
 * and replace name-derived vanity suffixes with a fixed mask. Path-absolute inputs (leading `/`)
 * stay path-shaped; other non-URL strings only get ID sanitization.
 */
export function sanitizeAmplitudeUrl(url: string): string {
  if (!/^https?:\/\//.test(url) && !url.startsWith('/')) {
    return sanitizeBenchlingIds(url);
  }

  try {
    const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://benchling.com';
    const parsed = new URL(url, baseOrigin);
    parsed.search = '';
    parsed.hash = '';
    parsed.pathname = sanitizePathSegments(parsed.pathname);
    if (url.startsWith('/')) {
      return parsed.pathname;
    }
    return parsed.toString();
  } catch (error) {
    if (error instanceof TypeError) {
      return sanitizeBenchlingIds(url);
    }
    throw error;
  }
}

function maskReadableTextProperties(sanitized: Record<string, unknown>): void {
  for (const key of READABLE_TEXT_PROPERTY_KEYS) {
    const value = sanitized[key];
    if (typeof value === 'string' && value.length > 0 && value !== MASKED_TEXT) {
      sanitized[key] = MASKED_TEXT;
    }
  }
}

function filterElementAttributes(sanitized: Record<string, unknown>): void {
  const attributes = sanitized[ELEMENT_ATTRIBUTES_PROPERTY_KEY];
  if (attributes == null) {
    return;
  }
  const filtered = pick(attributes as Record<string, unknown>, ...ALLOWED_ELEMENT_ATTRIBUTE_KEYS);
  if (Object.keys(filtered).length === 0) {
    delete sanitized[ELEMENT_ATTRIBUTES_PROPERTY_KEY];
    return;
  }
  sanitized[ELEMENT_ATTRIBUTES_PROPERTY_KEY] = filtered;
}

function filterElementHierarchyNode(node: Record<string, unknown>): Record<string, unknown> {
  const filtered = pick(node, ...ALLOWED_ELEMENT_HIERARCHY_NODE_KEYS);
  if (node.attrs != null) {
    const attrs = pick(node.attrs as Record<string, unknown>, ...ALLOWED_ELEMENT_HIERARCHY_ATTRIBUTE_KEYS);
    if (Object.keys(attrs).length > 0) {
      filtered.attrs = attrs;
    }
  }
  return filtered;
}

function filterElementHierarchy(sanitized: Record<string, unknown>): void {
  const hierarchy = sanitized[ELEMENT_HIERARCHY_PROPERTY_KEY];
  if (hierarchy == null) {
    return;
  }
  sanitized[ELEMENT_HIERARCHY_PROPERTY_KEY] = (hierarchy as Array<Record<string, unknown>>).map((node) =>
    filterElementHierarchyNode(node)
  );
}

function sanitizeProperties(
  properties: Record<string, unknown> | undefined,
  redactionLevel: AmplitudeRedactionLevel
): Record<string, unknown> | undefined {
  if (!properties) {
    return properties;
  }

  const sanitized: Record<string, unknown> = {...properties};
  for (const key of DROPPED_EVENT_PROPERTY_KEYS) {
    delete sanitized[key];
  }

  filterElementAttributes(sanitized);
  filterElementHierarchy(sanitized);

  for (const key of SANITIZED_URL_PROPERTY_KEYS) {
    if (key in sanitized && typeof sanitized[key] === 'string') {
      sanitized[key] = sanitizeAmplitudeUrl(sanitized[key]);
    }
  }

  for (const key of SANITIZED_TEXT_PROPERTY_KEYS) {
    if (key in sanitized && typeof sanitized[key] === 'string') {
      sanitized[key] = sanitizeBenchlingIds(sanitized[key]);
    }
  }

  if (redactionLevel === AmplitudeRedactionLevel.MASK_READABLE_TEXT) {
    maskReadableTextProperties(sanitized);
  }

  return sanitized;
}

function getRedactionLevelFromProperties(
  eventProperties: Record<string, unknown> | undefined
): AmplitudeRedactionLevel {
  const pagePath = eventProperties?.['Page Path'] ?? eventProperties?.['[Amplitude] Page Path'];
  if (typeof pagePath === 'string') {
    return getRouteRedactionLevel(pagePath);
  }
  return getRouteRedactionLevel();
}

export interface AmplitudePrivacyReadyLatch {
  waitUntilReady: () => Promise<void>;
  markReady: () => void;
}

export function createAmplitudePrivacyReadyLatch(): AmplitudePrivacyReadyLatch {
  let ready = false;
  let resolvers: Array<() => void> = [];
  return {
    waitUntilReady: (): Promise<void> => {
      if (ready) {
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        resolvers.push(resolve);
      });
    },
    markReady: (): void => {
      ready = true;
      for (const resolve of resolvers) {
        resolve();
      }
      resolvers = [];
    },
  };
}

/**
 * Before-phase plugin that holds events until the privacy enrichment plugin is installed.
 * Performs no redaction — enrichment owns all sanitization.
 */
export function createAmplitudePrivacyBeforePlugin(waitUntilReady: () => Promise<void>): BeforePlugin {
  return {
    name: 'benchling-amplitude-privacy-before',
    type: 'before',
    execute: async (event: AmplitudeEvent): Promise<AmplitudeEvent | null> => {
      await waitUntilReady();
      return event;
    },
  };
}

/** Enrichment plugin that sanitizes event properties on all Amplitude events during rollout. */
export function createAmplitudePrivacyEnrichmentPlugin(): EnrichmentPlugin {
  return {
    name: 'benchling-amplitude-privacy',
    type: 'enrichment',
    execute: async (event: AmplitudeEvent): Promise<AmplitudeEvent | null> => {
      // Event includes RevenueEvent, whose event_properties aren't freely string-indexable.
      const eventProperties = event.event_properties as Record<string, unknown> | undefined;
      const redactionLevel = getRedactionLevelFromProperties(eventProperties);
      return await Promise.resolve({
        ...event,
        event_properties: sanitizeProperties(eventProperties, redactionLevel),
      });
    },
  };
}
